import { EVERYCRED_LINKS } from '@constants/app.constants';
import { environment } from '@environment/environment';
import { TenantBootstrap } from '@models/bootstrap.model';

/**
 * Path of the public bootstrap endpoint, appended to `environment.apiUrl`
 * (which already ends in `/v1`), e.g. `https://.../v1/public/bootstrap`.
 */
export const BOOTSTRAP_API_PATH = '/public/bootstrap';

/**
 * Max time to wait for the bootstrap call before falling back to defaults.
 *
 * Kept deliberately short: this runs in `provideAppInitializer`, so it blocks
 * first paint. 1.5s is enough for a healthy-but-slow server to deliver branded
 * content, while capping the worst-case blank-screen wait on a hung endpoint.
 */
export const BOOTSTRAP_TIMEOUT_MS = 1500;

/**
 * Default brand logo shown on the auth screens when the tenant provides none.
 * Matches the asset the auth templates hardcoded before this feature, so the
 * default experience is visually identical to before.
 */
export const DEFAULT_LOGO = 'assets/icons/logo.svg';

/** Default favicon (same file referenced by index.html). */
export const DEFAULT_FAVICON = 'favicon.ico';

/**
 * Platform-default bootstrap config.
 *
 * Used before `/public/bootstrap` responds and whenever a field comes back
 * `null`. Values mirror the previously hardcoded EveryCRED branding so the app
 * looks unchanged until a tenant supplies its own.
 *
 * `footerText` defaults to `null` (no extra footer line) so the footer renders
 * exactly as it did before for the default tenant.
 */
export const DEFAULT_BOOTSTRAP: TenantBootstrap = {
  tenant: null,
  branding: {
    logo: null,
    logoWhite: null,
    logoDark: null,
    favicon: null,
    loginBanner: null,
    backgroundImage: null
  },
  theme: {
    mode: 'light',
    primaryColor: null,
    secondaryColor: null,
    accentColor: null,
    fontFamily: null,
    fontUrl: null
  },
  login: {
    title: null,
    subtitle: null
  },
  legal: {
    footerText: null,
    copyright: `© ${new Date().getFullYear()} EveryCRED. All rights reserved.`,
    privacyPolicyUrl: 'https://everycred.com/privacy-policy/',
    termsUrl: 'https://everycred.com/terms-of-service/'
  },
  support: {
    email: environment.email,
    phone: null,
    helpUrl: EVERYCRED_LINKS.SUPPORT
  },
  i18n: {
    defaultLanguage: 'en',
    supportedLanguages: ['en', 'de']
  },
  features: {
    selfRegistration: true
  },
  security: {
    recaptchaSiteKey: null
  }
};
